import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  inject,
  signal,
  computed,
  input,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucidePlay,
  lucideTriangleAlert,
  lucideLoader,
  lucideRefreshCw,
  lucideFileImage,
  lucideBox,
  lucideFile,
  lucideImage,
  lucideCircleCheck,
  lucideChevronRight,
  lucideLayoutTemplate,
} from '@ng-icons/lucide';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { BrnDialogContentDirective } from '@spartan-ng/brain/dialog';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmBadgeDirective } from '@spartan-ng/helm/badge';
import {
  HlmAlertDirective,
  HlmAlertDescriptionDirective,
  HlmAlertIconDirective,
  HlmAlertTitleDirective,
} from '@spartan-ng/helm/alert';
import {
  Analysis,
  FileOverviewItem,
  PlanningTemplateOption,
  SelectMediaDto,
} from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { AddressService } from '@shared/services/address.service';
import { AnalysisTypePicker } from '@shared/components/analysis-type-picker/analysis-type-picker';

interface SelectableFile {
  id: string;
  type: '2d' | '3d';
  label: string;
  icon: string;
  filename: string;
  size: string;
  date: string;
}

export function requiresSolarPlanningOverride(
  analysis: Analysis,
  taskWindow: { startTask: number; endTask: number },
): boolean {
  return (
    analysis.address?.hasSolar === true &&
    taskWindow.startTask <= 5 &&
    taskWindow.endTask >= 4
  );
}

function fileTagLabel(tag: string): string {
  const labels: Record<string, string> = {
    satellite_image: 'Satellitenbild',
    satellite_image_with_pv: 'Satellitenbild mit PV',
    satellite_image_with_marker: 'Satellitenbild mit Marker',
    '3d_model': '3D-Modell',
    elevation_model: 'Höhenmodell',
    point_cloud: 'Punktwolke',
  };
  return labels[tag] ?? tag;
}

function fileIcon(contentType?: string): string {
  if (!contentType) return 'lucideFile';
  if (contentType.startsWith('image/')) return 'lucideFileImage';
  if (contentType.includes('octet-stream') || contentType.includes('model') || contentType.includes('ply'))
    return 'lucideBox';
  return 'lucideFile';
}

function formatSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

@Component({
  selector: 'app-start-analysis-dialog',
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    NgIconComponent,
    ...HlmDialogImports,
    HlmButtonDirective,
    HlmBadgeDirective,
    HlmAlertDirective,
    HlmAlertDescriptionDirective,
    HlmAlertIconDirective,
    HlmAlertTitleDirective,
    BrnDialogContentDirective,
    AnalysisTypePicker,
  ],
  providers: [
    provideIcons({
      lucidePlay,
      lucideTriangleAlert,
      lucideLoader,
      lucideRefreshCw,
      lucideFileImage,
      lucideBox,
      lucideFile,
      lucideImage,
      lucideCircleCheck,
      lucideChevronRight,
      lucideLayoutTemplate,
    }),
  ],
  templateUrl: './start-analysis-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StartAnalysisDialog {
  private readonly analysisService = inject(AnalysisService);
  private readonly addressService = inject(AddressService);
  private confirmedClose = false;
  private consumptionEdited = false;
  private priceEdited = false;

  // Signal inputs — set via ComponentRef.setInput() in StartAnalysisDialogService
  readonly analysis = input.required<Analysis>();
  readonly organizationId = input.required<string>();
  readonly filesOverview = input<FileOverviewItem[]>([]);
  readonly currentSelectedMedia = input<SelectMediaDto | null>(null);
  readonly currentSelectedTemplateId = input<string | null>(null);
  readonly taskWindow = input.required<{ startTask: number; endTask: number }>();
  readonly requiresMedia = input(false);
  readonly requiresTemplate = input(false);
  /**
   * The run starts at geocut, so the analysis file does not exist yet — geocut
   * produces it. There is nothing to select; the user picks the type it should
   * produce instead, which is stored as the analysis type.
   */
  readonly prospective = input(false);

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  state = signal<'open' | 'closed'>('closed');
  isLoading = signal(false);
  error = signal<string | null>(null);
  errorType = signal<'invalid-state' | 'network' | null>(null);

  // Selected media state
  selectedMediaId = signal<string | null>(null);
  selectedMediaType = signal<'2d' | '3d' | null>(null);
  selectedTemplateId = signal<string | null>(null);
  templates = signal<PlanningTemplateOption[]>([]);
  isLoadingTemplates = signal(false);
  planningName = signal('Planung 1');
  planningPowerConsumption = signal(0);
  planningElectricityPrice = signal(0);
  ignoreExistingSolar = signal(false);
  requiresSolarOverride = computed(() =>
    requiresSolarPlanningOverride(this.analysis(), this.taskWindow()),
  );

  canConfirm = computed(() => {
    if (this.isLoading()) return false;
    if (this.requiresSolarOverride() && !this.ignoreExistingSolar()) return false;

    if (this.requiresMedia()) {
      if (this.prospective() && this.selectedMediaType() === null) return false;
      if (!this.prospective() && this.selectedMediaId() === null) return false;
    }
    if (
      this.requiresTemplate() &&
      (this.selectedTemplateId() === null ||
        !this.planningName().trim() ||
        this.planningPowerConsumption() <= 0 ||
        this.planningElectricityPrice() <= 0)
    ) return false;
    return !this.isLoadingTemplates();
  });

  readonly items2D = computed<SelectableFile[]>(() =>
    this.build2DItems(this.filesOverview()),
  );

  readonly items3D = computed<SelectableFile[]>(() =>
    this.build3DItems(this.filesOverview()),
  );

  readonly hasNoFiles = computed(
    () => this.items2D().length === 0 && this.items3D().length === 0,
  );

  readonly taskRangeLabel = computed(() => {
    const window = this.taskWindow();
    return window.startTask === window.endTask
      ? `Aufgabe ${window.startTask}`
      : `Aufgaben ${window.startTask}–${window.endTask}`;
  });

  open(): void {
    this.confirmedClose = false;
    this.consumptionEdited = false;
    this.priceEdited = false;
    this.state.set('open');
    this.error.set(null);
    this.ignoreExistingSolar.set(false);
    this.selectedTemplateId.set(this.currentSelectedTemplateId());
    const planningNumber = (this.analysis().plannings?.length ?? 0) + 1;
    this.planningName.set(`Planung ${planningNumber}`);
    this.planningPowerConsumption.set(this.analysis().powerConsumption ?? 0);
    this.planningElectricityPrice.set(this.analysis().electricityPrice ?? 0);
    // Reset to current selection
    const current = this.currentSelectedMedia();
    if (this.prospective()) {
      // Only the type carries over — the file it pointed at is about to be
      // regenerated by geocut.
      this.selectedMediaId.set(null);
      this.selectedMediaType.set(
        current?.mediaType ?? this.analysis()?.analysisType ?? null,
      );
    } else if (current) {
      this.selectedMediaId.set(current.mediaId);
      this.selectedMediaType.set(current.mediaType);
    } else {
      this.selectedMediaId.set(null);
      this.selectedMediaType.set(null);
    }
    if (this.requiresTemplate()) void this.loadTemplates();
  }

  close(): void {
    const emitCancelled = this.state() === 'open' && !this.confirmedClose;
    this.state.set('closed');
    if (emitCancelled) this.cancelled.emit();
  }

  selectFile(item: SelectableFile): void {
    this.selectedMediaId.set(item.id);
    this.selectedMediaType.set(item.type);
  }

  /** Prospective mode: no file id yet, only the type geocut should produce. */
  selectProspectiveType(type: '2d' | '3d'): void {
    this.selectedMediaType.set(type);
  }

  selectTemplate(templateId: string): void {
    if (this.isLoading()) return;
    this.selectedTemplateId.set(templateId);
    const template = this.templates().find((item) => item.id === templateId);
    if (!template) return;
    // Legacy response mappers represent missing values as zero. Valid analysis
    // inputs are positive; deliberate edits (including clearing a field) win.
    if (!(this.analysis().powerConsumption > 0) && !this.consumptionEdited) {
      this.planningPowerConsumption.set(template.powerConsumption ?? 0);
    }
    if (!(this.analysis().electricityPrice > 0) && !this.priceEdited) {
      this.planningElectricityPrice.set(template.electricityPrice ?? 0);
    }
  }

  setPlanningPowerConsumption(value: number): void {
    this.consumptionEdited = true;
    this.planningPowerConsumption.set(value);
  }

  setPlanningElectricityPrice(value: number): void {
    this.priceEdited = true;
    this.planningElectricityPrice.set(value);
  }

  getTemplateTypeLabel(type: PlanningTemplateOption['type']): string {
    return type === 'lease' ? 'Miete' : 'Kauf';
  }

  async confirm(): Promise<void> {
    if (!this.canConfirm()) return;

    const mediaId = this.selectedMediaId();
    const mediaType = this.selectedMediaType();

    if (this.requiresMedia() && !mediaType) return;
    if (this.requiresMedia() && !this.prospective() && !mediaId) return;

    // Store previous selection for rollback if API fails
    const previousMediaId = this.currentSelectedMedia()?.mediaId || null;
    const previousMediaType = this.currentSelectedMedia()?.mediaType || null;

    this.isLoading.set(true);
    this.error.set(null);
    this.errorType.set(null);

    try {
      // Persist the choice on the SI analysis record — the worker reads it off
      // the row when it gets there. Executing is the caller's job.
      if (this.requiresMedia() && this.prospective()) {
        // No file to point at yet; record the type geocut should produce.
        await this.analysisService.updateAnalysis(
          this.organizationId(),
          this.analysis().analysisId,
          { analysisType: mediaType! },
        );
      } else if (this.requiresMedia()) {
        await this.analysisService.selectMedia(
          this.organizationId(),
          this.analysis().analysisId,
          { mediaId: mediaId!, mediaType: mediaType! },
        );
      }

      if (this.requiresTemplate()) {
        const requestedAt = new Date().toISOString();
        const existingRequests = this.analysis().metadata?.['planningNameRequests'];
        await this.analysisService.updateAnalysis(
          this.organizationId(),
          this.analysis().analysisId,
          {
            powerConsumption: this.planningPowerConsumption(),
            electricityPrice: this.planningElectricityPrice(),
            customFields: {
              planningInputsChangedAt: requestedAt,
              planningNameRequests: [
                ...(Array.isArray(existingRequests) ? existingRequests : []),
                {
                  name: this.planningName().trim(),
                  requestedAt,
                  powerConsumption: this.planningPowerConsumption(),
                  electricityPrice: this.planningElectricityPrice(),
                  templateId: this.selectedTemplateId()!,
                },
              ],
            },
          } as any,
        );
        await this.analysisService.selectTemplate(
          this.organizationId(),
          this.analysis().analysisId,
          { templateId: this.selectedTemplateId()! },
        );
      } else if (this.requiresMedia()) {
        await this.analysisService.updateAnalysis(
          this.organizationId(),
          this.analysis().analysisId,
          { customFields: { planningInputsChangedAt: new Date().toISOString() } } as any,
        );
      }

      if (this.requiresSolarOverride() && this.ignoreExistingSolar()) {
        const addressId = this.analysis().addressId;
        if (!addressId) throw new Error('Adresse der Analyse fehlt');
        await this.addressService.updateAddress(this.organizationId(), addressId, {
          hasSolar: false,
        });
      }

      this.confirmedClose = true;
      this.state.set('closed');
      this.confirmed.emit();
    } catch (err: any) {
      // Rollback to previous selection on error
      if (previousMediaId) {
        this.selectedMediaId.set(previousMediaId);
        this.selectedMediaType.set(previousMediaType);
      } else {
        this.selectedMediaId.set(null);
        this.selectedMediaType.set(null);
      }

      const errorMsg =
        err?.error?.message ||
        (this.prospective()
          ? 'Fehler beim Auswählen der Analyseart'
          : 'Fehler beim Auswählen der Datei');

      if (err?.status === 422) {
        this.error.set(errorMsg);
        this.errorType.set('invalid-state');
      } else if (!navigator.onLine) {
        this.error.set('Keine Internetverbindung vorhanden');
        this.errorType.set('network');
      } else {
        this.error.set(errorMsg);
        this.errorType.set('network');
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async retry(): Promise<void> {
    await this.confirm();
  }

  private build2DItems(filesOverview: FileOverviewItem[]): SelectableFile[] {
    if (!filesOverview?.length) return [];
    return filesOverview
      .filter(
        (f) =>
          f.exists &&
          f.file &&
          (f.tag === 'satellite_image' ||
            f.tag === 'satellite_image_with_pv' ||
            f.tag === 'satellite_image_with_marker'),
      )
      .map((f) => ({
        id: f.file!.id || f.file!.key || f.tag,
        type: '2d' as const,
        label: fileTagLabel(f.tag),
        icon: fileIcon(f.file?.contentType) || 'lucideFileImage',
        filename: f.file?.filename ?? '',
        size: formatSize(f.file?.size),
        date: formatDate(f.file?.uploadedAt),
      }));
  }

  private build3DItems(filesOverview: FileOverviewItem[]): SelectableFile[] {
    if (!filesOverview?.length) return [];
    return filesOverview
      .filter((f) => {
        if (!f.exists || !f.file) return false;
        if (f.tag === '3d_model') return true;
        const ct = (f.file.contentType ?? '').toLowerCase();
        const fn = (f.file.filename ?? '').toLowerCase();
        return ct.includes('ply') || fn.endsWith('.ply');
      })
      .map((f) => ({
        id: f.file!.id || f.file!.key || `${f.tag}-${f.file!.filename}`,
        type: '3d' as const,
        label: fileTagLabel(f.tag),
        icon: 'lucideBox',
        filename: f.file?.filename ?? '',
        size: formatSize(f.file?.size),
        date: formatDate(f.file?.uploadedAt),
      }));
  }

  private async loadTemplates(): Promise<void> {
    this.isLoadingTemplates.set(true);
    try {
      const templates = await this.analysisService.getPlanningTemplates(
        this.organizationId(),
        this.analysis().analysisId,
      );
      const availableTemplates = templates.filter((template) => !template.deletedAt);
      this.templates.set(availableTemplates);
      const selectedTemplate =
        availableTemplates.find((template) => template.id === this.selectedTemplateId()) ??
        availableTemplates.find((template) => template.hubMetadata?.favorite) ??
        availableTemplates[0];
      if (selectedTemplate) this.selectTemplate(selectedTemplate.id);
    } catch {
      this.templates.set([]);
      this.error.set('Planungsvorlagen konnten nicht geladen werden');
    } finally {
      this.isLoadingTemplates.set(false);
    }
  }
}
